import type { ReactNode } from "react";
import { SweepButton } from "@softmax/design-system";
import type { ProfileEditorProps } from "../use-profile-settings";
import type { AgentRow } from "../agent-rows";
import { sectionLabel, bodyText } from "./profile-styles";
import { ProfilePicture } from "./profile-picture";
import { ProfileField } from "./profile-field";

function AgentFields({
  editor,
  row,
  index,
}: ProfileEditorProps & { row: AgentRow; index: number }) {
  const { players, writes } = editor;
  const slug = row.id ?? "new";
  const player = row.player;
  let trailing: ReactNode = null;

  if (player && !row.isDefault) {
    trailing = (
      <SweepButton
        quiet
        type="button"
        focus="ring"
        disabled={writes.locked}
        onClick={() => players.makeDefault(player)}
      >
        Make default
      </SweepButton>
    );
  } else if (players.rows.length > 1 && row.isDefault) {
    trailing = <span className={bodyText}>Default agent</span>;
  }

  return (
    <fieldset
      aria-label={row.savedName || "New agent"}
      className={`flex min-w-0 flex-col gap-[24px] ${index > 0 ? "border-t border-[rgba(22,22,22,0.13)] pt-[24px]" : ""}`}
    >
      <ProfilePicture
        inputId={`account-agent-icon-${slug}`}
        src={row.avatarUrl}
        initial={(row.savedName || row.name).trim().charAt(0).toUpperCase()}
        changeLabel="Change icon"
        removeLabel="Remove icon"
        disabled={writes.locked || player === null}
        disabledTitle="Name your agent first"
        busy={writes.busy}
        onPick={(file) => {
          if (player) players.updatePhoto({ player, file });
        }}
        onRemove={() => {
          if (player) players.updatePhoto({ player, file: null });
        }}
        trailing={trailing}
        status={writes.notices[`agent-row-${slug}`] ?? null}
      />
      <ProfileField
        id={`account-agent-name-${slug}`}
        label="Agent name"
        saved={row.savedName}
        draft={row.name}
        onDraft={(value) => players.setName({ row, value })}
        onSave={() => players.saveName(row)}
        onCancel={() => players.setName({ row, value: null })}
        status={writes.notices[`agent-name-${slug}`] ?? null}
        disabled={writes.locked}
        saveLabel={row.id === null ? "Create agent" : "Save"}
        autoComplete="off"
        maxLength={64}
      />
    </fieldset>
  );
}

export function ProfileAgents({ editor }: ProfileEditorProps) {
  const { players, writes } = editor;
  return (
    <section
      aria-labelledby="account-agent"
      className="flex flex-1 flex-col gap-[24px] border-t border-[rgba(22,22,22,0.13)] pt-[24px]"
    >
      <h2 id="account-agent" className={sectionLabel}>
        {players.rows.length > 1 ? "Your agents" : "Your agent"}
      </h2>
      {players.hasSavedAgents && players.newName !== "" ? (
        <div className="flex flex-wrap items-center gap-[12px]">
          <p className={bodyText}>
            Agent name draft kept: {players.newName}. Review your saved agents
            below, then discard this draft if it is no longer needed.
          </p>
          <SweepButton
            quiet
            type="button"
            focus="ring"
            disabled={writes.locked}
            onClick={players.discardNewName}
          >
            Discard draft
          </SweepButton>
        </div>
      ) : null}
      {players.rows.map((row, index) => (
        <AgentFields
          key={row.id ?? "new"}
          editor={editor}
          row={row}
          index={index}
        />
      ))}
    </section>
  );
}
